import { NorthstarEndpoints } from "@recore-solutions/nstar-lib";
import { AuthConfig } from "angular-oauth2-oidc";

export interface LoonEnvironment {
	name: LoonEnvironmentName;
	production: boolean;
	version: string;
	hostname: string;
	oAuth: AuthConfig;
	urlRoot: string;
	pendo: {
		apiKey: string;
	};
	endpoints: {
		session: string;
		logout: string;
		searchSuggestions: string;
		searchingMember: string;
		listing: string;
		upsertListings: string;
		timestampListing: string;
		specialUpsertListings: string;
		dynamicData: string;
		genericProc: string;
		codeTables: string;
		listingAudit: string;
		listingHistory: string;
		listingSummary: string;
		listingOperations: string;
		openHouses: string;
		openHouseHistory: string;
		media: string;
		mediaUpload: string;
		mediaDisplayRoot: string;
		mediaDisplay: string;
		mediaHistory: string;
		addEditMedia: string;
		help: string;
		autopopSearch: string;
		autopop: string;
		rprAutopop: string;
		taxData: string;
		validation: string;
		specialValidation: string;
		duplicateListings: string;
		postcardmaniaUrl: string;
		adbuilderUrl: string;
		spherebuilderUrl: string;
		adbuilderCampaigns: string;
		preferences: string;
		wellcomematUrl: string;
		uiComponents: string;
		member: Pick<NorthstarEndpoints, "get">;
		memberCoverage: NorthstarEndpoints;
		sentrilock: string;
		supra: string;
		gbr: string;
		rentspree: string;
		listingCity: string;
	};
	google: {
		analytics: {
			trackingId: string;
		};
	};
}

export enum LoonEnvironmentName {
	LOCAL = "LOCAL",
	PROXY = "PROXY",
	DEV = "DEV",
	SSO_DEV = "DEV",
	TEST = "TEST",
	TEST_BK = "TEST-BK",
	STAGE = "STAGE",
	STAGE_BK = "STAGE-BK",
	PROD = "PROD",
	PROD_BK = "PROD-BK",
}

// Builds consistent environment objects, with the option to override endpoints.
export function LoonEnvironmentFactory(
	name: LoonEnvironmentName,
	production: boolean,
	version: string,
	urlRoot: string,
	oAuth: AuthConfig,
	fileSystem: string,
	trackingId: string,
	options?: Partial<LoonEnvironment>
): LoonEnvironment {
	return {
		name,
		production,
		version,
		hostname: "localhost01",
		oAuth,
		urlRoot,
		pendo: {
			apiKey: options?.pendo?.apiKey || "",
		},
		endpoints: {
			session: urlRoot + "session-api/api/v2",
			logout: "https://signin.northstarmls.com/Account/Logout",
			searchSuggestions: urlRoot + "listings-api/api/v2/search-suggestions",
			searchingMember: urlRoot + "listings-api/api/v2/searching-member",
			listing: urlRoot + "listings-api/api/v2/listings-detail",
			upsertListings: urlRoot + "listings-api/api/v2/listings",
			timestampListing: urlRoot + "listings-api/api/v2/timestamp",
			specialUpsertListings: urlRoot + "listings-api/api/v2/special-edit",
			dynamicData: urlRoot + "listings-api/api/v2/dynamic-data",
			genericProc: urlRoot + "listings-api/api/v2/get-generic",
			codeTables: urlRoot + "listings-api/api/v2/code-tables",
			listingSummary: urlRoot + "listings-api/api/v2/listings-summary",
			listingOperations: urlRoot + "listings-api/api/v2",
			openHouseHistory: urlRoot + "open-house-api/api/v2/open-house-history",
			openHouses: urlRoot + "open-house-api/api/v2/open-houses",
			media: urlRoot + "add-edit-media/api/v2/media",
			mediaUpload: urlRoot + "add-edit-media/api/v2/upload",
			mediaDisplayRoot: fileSystem + "mediadisplay",
			mediaDisplay: fileSystem + "mediadisplay/v2",
			mediaHistory: urlRoot + "add-edit-media/api/v2/media-history",
			addEditMedia: urlRoot + "add-edit-media/api/v2",
			help: urlRoot + "help-api/api/v2/help",
			autopopSearch: urlRoot + "listings-api/api/v1/autopop",
			autopop: urlRoot + "listings-api/api/v2/autopopulate",
			rprAutopop: urlRoot + "listings-api/api/v1/rprAutopopulate",
			taxData: urlRoot + "listings-api/api/v2/autopop/tax-data",
			validation: urlRoot + "listings-api/api/v2/validate",
			specialValidation: urlRoot + "listings-api/api/v2/special-validate",
			duplicateListings: urlRoot + "listings-api/api/v2/duplicate-listings",
			postcardmaniaUrl: urlRoot + "marketing-api/api/v2/postcardmania-url",
			adbuilderUrl: urlRoot + "marketing-api/api/v2/adbuilder-url",
			spherebuilderUrl: urlRoot + "marketing-api/api/v2/spherebuilder-url",
			adbuilderCampaigns: urlRoot + "marketing-api/api/v2/adbuilder-campaigns",
			preferences: urlRoot + "session-api/api/v2/sessions/preferences",
			wellcomematUrl: urlRoot + "session-api/api/v2/wellcomemat/url",
			uiComponents: urlRoot + "listings-api/api/v2/application-ui-component",
			listingHistory: urlRoot + "history-api/api/v2/listing-history",
			listingAudit: urlRoot + "history-api/api/v2/listing-audit",
			member: {
				get: urlRoot + "roster-api/api/v2/member",
			},
			memberCoverage: {
				get: urlRoot + "roster-api/api/v2/member-coverage",
				upsert: urlRoot + "roster-api/api/v2/member-coverage",
			},
			listingCity: urlRoot + "listings-api/api/v2/listing-city",
			sentrilock: urlRoot + "sentrilock-api/api/v1/",
			supra: urlRoot + "supra-api/api/v1/",
			gbr: urlRoot + "gbr-api/api/v1/",
			rentspree: urlRoot + "rentspree-api/api/v1/",
			...(options?.endpoints || {}),
		},
		google: {
			analytics: {
				trackingId,
			},
		},
	};
}
